import type {
  ChatItem,
  ChatSidebarBlock,
  ChatSidebarStructuredContent,
  MessageGroup,
  ToolCard,
  ToolClusterItem,
  ToolClusterStatus,
} from "../types/chat-types.ts";
import { extractTextCached, extractThinkingCached } from "./message-extract.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "./message-normalizer.ts";
import {
  buildRawTextFromBlocks,
  buildRichBlocksFromMessage,
  buildRichBlocksFromText,
} from "./rich-blocks.ts";
import type { JsonPreview } from "./rich-blocks.ts";
import { extractToolCards } from "./tool-cards.ts";

export type MessageImageBlock = {
  url: string;
  alt?: string;
};

export type VisibilityOptions = {
  showToolCalls?: boolean;
  showReasoning?: boolean;
};

type MessageItem = Extract<ChatItem, { kind: "message" }>;

const TOOL_CALL_BLOCK_TYPES = new Set(["toolcall", "tool_call", "tooluse", "tool_use"]);
const TOOL_RESULT_BLOCK_TYPES = new Set(["toolresult", "tool_result", "tool_result_error"]);
const TOOL_BLOCK_TYPES = new Set([...TOOL_CALL_BLOCK_TYPES, ...TOOL_RESULT_BLOCK_TYPES]);

function normalizeContentBlockType(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function getTrimmedRecordString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isToolContentBlock(block: unknown): boolean {
  if (!block || typeof block !== "object") {
    return false;
  }
  const entry = block as Record<string, unknown>;
  return TOOL_BLOCK_TYPES.has(normalizeContentBlockType(entry.type));
}

function cloneSegmentMessage(
  message: Record<string, unknown>,
  content: unknown[],
  preserveToolMeta: boolean,
): Record<string, unknown> {
  const clone: Record<string, unknown> = {
    ...message,
    content,
  };
  if (!preserveToolMeta) {
    delete clone.toolCallId;
    delete clone.tool_call_id;
    delete clone.toolName;
    delete clone.tool_name;
    delete clone.toolState;
  }
  return clone;
}

function hasVisibleMessageText(message: Record<string, unknown>): boolean {
  return Boolean(extractTextCached(message)?.trim() || extractThinkingCached(message)?.trim());
}

export function extractImages(message: unknown): MessageImageBlock[] {
  const m = message as Record<string, unknown>;
  const content = m.content;
  const images: MessageImageBlock[] = [];

  if (!Array.isArray(content)) {
    return images;
  }

  for (const block of content) {
    if (typeof block !== "object" || block === null) {
      continue;
    }
    const entry = block as Record<string, unknown>;

    if (entry.type === "image") {
      const source = entry.source as Record<string, unknown> | undefined;
      if (source?.type === "base64" && typeof source.data === "string") {
        const data = source.data;
        const mediaType = (source.media_type as string) || "image/png";
        const url = data.startsWith("data:") ? data : `data:${mediaType};base64,${data}`;
        images.push({ url });
        continue;
      }
      if (typeof entry.url === "string") {
        images.push({ url: entry.url });
        continue;
      }
    }

    if (entry.type === "image_url") {
      const imageUrl = entry.image_url as Record<string, unknown> | undefined;
      if (typeof imageUrl?.url === "string") {
        images.push({ url: imageUrl.url });
      }
    }
  }

  return images;
}

export function extractGroupText(group: MessageGroup): string {
  const parts: string[] = [];
  for (const { message } of group.messages) {
    const text = extractTextCached(message);
    if (text?.trim()) {
      parts.push(text.trim());
    }
  }
  return parts.join("\n\n");
}

export { detectJsonPreview } from "./rich-blocks.ts";
export type { JsonPreview } from "./rich-blocks.ts";

export function buildMessageSidebarBlocks(params: {
  message?: unknown;
  text?: string | null;
  images?: MessageImageBlock[];
}): ChatSidebarBlock[] {
  const images = params.images ?? [];
  const blocks = params.message
    ? buildRichBlocksFromMessage(params.message)
    : buildRichBlocksFromText(params.text?.trim() ?? "");

  return [
    ...blocks,
    ...images.map((image) => ({
      type: "image" as const,
      url: image.url,
      alt: image.alt,
    })),
  ];
}

export function buildStructuredSidebarContent(params: {
  title: string;
  message?: unknown;
  text?: string | null;
  images?: MessageImageBlock[];
  jsonResult?: JsonPreview | null;
}): ChatSidebarStructuredContent {
  const text = params.text?.trim() ?? "";
  const images = params.images ?? [];
  const blocks = buildMessageSidebarBlocks({
    message: params.message,
    text: text || (params.jsonResult ? params.jsonResult.pretty : null),
    images,
  });

  if (params.jsonResult) {
    const labeledJsonBlocks = blocks.filter(
      (block): block is Extract<ChatSidebarBlock, { type: "json" }> =>
        block.type === "json" && !block.label,
    );
    if (labeledJsonBlocks.length > 0) {
      for (const block of labeledJsonBlocks) {
        block.label = jsonSummaryLabel(block.data);
      }
    } else if (blocks.length === 0) {
      blocks.push({
        type: "json",
        label: jsonSummaryLabel(params.jsonResult.parsed),
        data: params.jsonResult.parsed,
      });
    }
  }

  const hasTypedRichBlocks = blocks.some(
    (block) =>
      block.type === "diagram-mermaid" ||
      block.type === "chart-vega-lite" ||
      block.type === "math-latex" ||
      block.type === "embedded-markdown" ||
      block.type === "svg" ||
      block.type === "iframe",
  );

  return {
    kind: "structured",
    title: params.title,
    blocks,
    rawText: hasTypedRichBlocks ? buildRawTextFromBlocks(blocks) : text || buildRawTextFromBlocks(blocks),
  };
}

export function jsonSummaryLabel(parsed: unknown): string {
  if (Array.isArray(parsed)) {
    return `Array (${parsed.length} item${parsed.length === 1 ? "" : "s"})`;
  }
  if (parsed && typeof parsed === "object") {
    const keys = Object.keys(parsed as Record<string, unknown>);
    if (keys.length <= 4) {
      return `{ ${keys.join(", ")} }`;
    }
    return `Object (${keys.length} keys)`;
  }
  return "JSON";
}

export function hasVisibleMessageContent(message: unknown, opts?: VisibilityOptions): boolean {
  const showToolCalls = opts?.showToolCalls ?? true;
  const showReasoning = opts?.showReasoning ?? false;

  const text = extractTextCached(message);
  if (text?.trim()) {
    return true;
  }
  if (showReasoning) {
    const thinking = extractThinkingCached(message);
    if (thinking?.trim()) {
      return true;
    }
  }
  const toolCards = showToolCalls ? extractToolCards(message) : [];
  if (toolCards.length > 0) {
    return true;
  }
  if (buildRichBlocksFromMessage(message).length > 0) {
    return true;
  }
  return extractImages(message).length > 0;
}

export function hasVisibleGroupContent(group: MessageGroup, opts?: VisibilityOptions): boolean {
  return group.messages.some(({ message }) => hasVisibleMessageContent(message, opts));
}

export function splitMessageItemForToolClustering(item: MessageItem): MessageItem[] {
  const message = item.message as Record<string, unknown>;
  const content = Array.isArray(message.content) ? message.content : null;
  if (!content || content.length === 0) {
    return [item];
  }

  let hasToolBlock = false;
  let hasNonToolBlock = false;
  for (const block of content) {
    if (isToolContentBlock(block)) {
      hasToolBlock = true;
    } else {
      hasNonToolBlock = true;
    }
  }
  if (!hasToolBlock || !hasNonToolBlock) {
    return [item];
  }

  const hasVisibleText = hasVisibleMessageText(message);
  const parts: MessageItem[] = [];
  let buffer: unknown[] = [];
  let currentIsTool: boolean | null = null;
  let partIndex = 0;

  const flush = () => {
    if (buffer.length === 0 || currentIsTool == null) {
      return;
    }
    const nextContent = [...buffer];
    const onlyEmptyTextBlocks =
      !currentIsTool &&
      !hasVisibleText &&
      nextContent.every((block) => {
        if (!block || typeof block !== "object") {
          return false;
        }
        const entry = block as Record<string, unknown>;
        const type = normalizeContentBlockType(entry.type);
        return type === "text" && typeof entry.text === "string" && entry.text.trim().length === 0;
      });
    if (!onlyEmptyTextBlocks) {
      parts.push({
        kind: "message",
        key: `${item.key}:part:${partIndex++}:${currentIsTool ? "tool" : "message"}`,
        message: cloneSegmentMessage(message, nextContent, currentIsTool),
      });
    }
    buffer = [];
  };

  for (const block of content) {
    const nextIsTool = isToolContentBlock(block);
    if (currentIsTool === null) {
      currentIsTool = nextIsTool;
    } else if (currentIsTool !== nextIsTool) {
      flush();
      currentIsTool = nextIsTool;
    }
    buffer.push(block);
  }
  flush();

  return parts.length > 0 ? parts : [item];
}

export function expandToolAwareItems(items: ChatItem[]): ChatItem[] {
  const expanded: ChatItem[] = [];
  for (const item of items) {
    if (item.kind !== "message") {
      expanded.push(item);
      continue;
    }
    expanded.push(...splitMessageItemForToolClustering(item));
  }
  return expanded;
}

function getMessageRunId(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const record = message as Record<string, unknown>;
  return getTrimmedRecordString(record, "runId");
}

function getMessageToolCallId(message: unknown): string | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const record = message as Record<string, unknown>;
  const topLevelId =
    getTrimmedRecordString(record, "toolCallId") ?? getTrimmedRecordString(record, "tool_call_id");
  if (topLevelId) {
    return topLevelId;
  }
  const content = Array.isArray(record.content) ? record.content : [];
  for (const block of content) {
    if (!block || typeof block !== "object") {
      continue;
    }
    const entry = block as Record<string, unknown>;
    const toolUseId =
      getTrimmedRecordString(entry, "toolUseId") ??
      getTrimmedRecordString(entry, "tool_use_id") ??
      getTrimmedRecordString(entry, "id");
    if (toolUseId) {
      return toolUseId;
    }
  }
  return null;
}

function getMessageToolStatus(message: unknown): ToolClusterStatus | null {
  if (!message || typeof message !== "object") {
    return null;
  }
  const record = message as Record<string, unknown>;
  const toolState = getTrimmedRecordString(record, "toolState")?.toLowerCase();
  if (toolState === "running" || toolState === "completed" || toolState === "partial") {
    return toolState;
  }
  return null;
}

function isToolMessageItem(item: ChatItem): item is MessageItem {
  return item.kind === "message" && extractToolCards(item.message).length > 0;
}

function buildToolClusterKey(messages: MessageItem[], index: number): string {
  const runId = messages.map(({ message }) => getMessageRunId(message)).find(Boolean);
  const toolCallId = messages.map(({ message }) => getMessageToolCallId(message)).find(Boolean);
  if (runId && toolCallId) {
    return `tool-cluster:${runId}:${toolCallId}`;
  }
  if (runId) {
    return `tool-cluster:${runId}:${messages[0].key}`;
  }
  if (toolCallId) {
    return `tool-cluster:${toolCallId}`;
  }
  return `tool-cluster:${messages[0]?.key ?? index}`;
}

function resolveToolClusterStatus(
  messages: MessageItem[],
  toolCards: ToolCard[],
): ToolClusterStatus {
  const explicitStates = messages
    .map(({ message }) => getMessageToolStatus(message))
    .filter(Boolean);
  if (explicitStates.includes("running")) {
    return "running";
  }
  if (explicitStates.includes("partial")) {
    return "partial";
  }
  if (explicitStates.includes("completed")) {
    return "completed";
  }

  const callCount = toolCards.filter((card) => card.kind === "call").length;
  const resultCount = toolCards.filter((card) => card.kind === "result").length;
  if (callCount === 0 && resultCount === 0) {
    return "partial";
  }
  if (callCount === 0 || resultCount >= callCount) {
    return "completed";
  }
  return "partial";
}

function resolveToolClusterSummary(status: ToolClusterStatus): string {
  switch (status) {
    case "running":
      return "正在使用工具";
    case "completed":
      return "工具使用完成";
    default:
      return "工具使用部分完成";
  }
}

function buildToolClusterItem(messages: MessageItem[], index: number): ToolClusterItem {
  const toolCards = messages.flatMap(({ message }) => extractToolCards(message));
  const status = resolveToolClusterStatus(messages, toolCards);
  const timestamp = normalizeMessage(messages[0]?.message).timestamp || Date.now();
  return {
    kind: "tool-cluster",
    key: buildToolClusterKey(messages, index),
    status,
    timestamp,
    toolCards,
    messages: messages.map(({ key, message }) => ({ key, message })),
    summary: resolveToolClusterSummary(status),
  };
}

function shouldAppendToToolCluster(cluster: MessageItem[], next: MessageItem): boolean {
  if (cluster.length === 0) {
    return true;
  }
  const clusterRunIds = [
    ...new Set(cluster.map(({ message }) => getMessageRunId(message)).filter(Boolean)),
  ];
  const nextRunId = getMessageRunId(next.message);
  if (clusterRunIds.length === 0 || !nextRunId) {
    return true;
  }
  return clusterRunIds.includes(nextRunId);
}

function clusterToolItems(items: ChatItem[], opts?: VisibilityOptions): ChatItem[] {
  const result: ChatItem[] = [];
  let currentCluster: MessageItem[] = [];
  let clusterIndex = 0;

  const flushCluster = () => {
    if (currentCluster.length === 0) {
      return;
    }
    result.push(buildToolClusterItem(currentCluster, clusterIndex++));
    currentCluster = [];
  };

  for (const item of items) {
    if (item.kind === "message" && !hasVisibleMessageContent(item.message, opts)) {
      continue;
    }
    if (isToolMessageItem(item)) {
      if (!shouldAppendToToolCluster(currentCluster, item)) {
        flushCluster();
      }
      currentCluster.push(item);
      continue;
    }
    flushCluster();
    result.push(item);
  }
  flushCluster();

  return result;
}

export function groupMessages(items: ChatItem[]): Array<ChatItem | MessageGroup> {
  const result: Array<ChatItem | MessageGroup> = [];
  let currentGroup: MessageGroup | null = null;

  for (const item of items) {
    if (item.kind !== "message") {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(item);
      continue;
    }

    const normalized = normalizeMessage(item.message);
    const role = normalizeRoleForGrouping(normalized.role);
    const senderLabel = role.toLowerCase() === "user" ? (normalized.senderLabel ?? null) : null;
    const timestamp = normalized.timestamp || Date.now();

    if (
      !currentGroup ||
      currentGroup.role !== role ||
      (role.toLowerCase() === "user" && currentGroup.senderLabel !== senderLabel)
    ) {
      if (currentGroup) {
        result.push(currentGroup);
      }
      currentGroup = {
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        senderLabel,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      };
    } else {
      currentGroup.messages.push({ message: item.message, key: item.key });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }
  return result;
}

export function buildTranscriptProjection(
  items: ChatItem[],
  opts?: VisibilityOptions,
): Array<ChatItem | MessageGroup> {
  const expandedItems = expandToolAwareItems(items);
  const visibilityOptions = {
    showToolCalls: opts?.showToolCalls ?? true,
    showReasoning: opts?.showReasoning ?? false,
  };
  const visibleBaseItems = expandedItems.filter((item) => {
    if (item.kind !== "message") {
      return true;
    }
    return hasVisibleMessageContent(item.message, visibilityOptions);
  });
  const visibleItems = visibilityOptions.showToolCalls
    ? clusterToolItems(visibleBaseItems, visibilityOptions)
    : visibleBaseItems.filter((item) => !isToolMessageItem(item));

  return groupMessages(visibleItems);
}

export function messageKey(message: unknown, index: number): string {
  const m = message as Record<string, unknown>;
  const toolCallId = typeof m.toolCallId === "string" ? m.toolCallId : "";
  if (toolCallId) {
    return `tool:${toolCallId}`;
  }
  const id = typeof m.id === "string" ? m.id : "";
  if (id) {
    return `msg:${id}`;
  }
  const messageId = typeof m.messageId === "string" ? m.messageId : "";
  if (messageId) {
    return `msg:${messageId}`;
  }
  const timestamp = typeof m.timestamp === "number" ? m.timestamp : null;
  const role = typeof m.role === "string" ? m.role : "unknown";
  if (timestamp != null) {
    return `msg:${role}:${timestamp}:${index}`;
  }
  return `msg:${role}:${index}`;
}

function isExportableTranscriptGroup(item: ChatItem | MessageGroup): item is MessageGroup {
  if (item.kind !== "group") {
    return false;
  }
  const role = normalizeRoleForGrouping(item.role);
  if (role !== "user" && role !== "assistant") {
    return false;
  }
  return Boolean(extractGroupText(item).trim());
}

export function buildExportTranscriptGroups(messages: unknown[]): MessageGroup[] {
  const items: ChatItem[] = [];
  const history = Array.isArray(messages) ? messages : [];

  for (let i = 0; i < history.length; i++) {
    const message = history[i];
    const normalized = normalizeMessage(message);
    const raw = message as Record<string, unknown>;
    const marker = raw.__openclaw as Record<string, unknown> | undefined;
    if (marker?.kind === "compaction") {
      items.push({
        kind: "divider",
        key:
          typeof marker.id === "string"
            ? `divider:compaction:${marker.id}`
            : `divider:compaction:${normalized.timestamp}:${i}`,
        label: "Compaction",
        timestamp: normalized.timestamp ?? Date.now(),
      });
      continue;
    }
    items.push({
      kind: "message",
      key: messageKey(message, i),
      message,
    });
  }

  return buildTranscriptProjection(items, {
    showToolCalls: true,
    showReasoning: false,
  }).filter(isExportableTranscriptGroup);
}
